import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Notification } from '@/types'
import api from '@/services/api'

export const useNotificationStore = defineStore('notification', () => {
  const notifications = ref<Notification[]>([])
  const unreadCount = ref(0)
  const loading = ref(false)
  const latestNotification = ref<Notification | null>(null)

  const hasUnread = computed(() => unreadCount.value > 0)

  async function fetchAll(): Promise<void> {
    loading.value = true
    try {
      const response = await api.get('/notifications')
      notifications.value = response.data.notifications || []
      unreadCount.value = response.data.unread || 0
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
    } finally {
      loading.value = false
    }
  }

  async function fetchLatest(): Promise<Notification | null> {
    try {
      const response = await api.get('/notifications/latest')
      latestNotification.value = response.data.notification || null
      unreadCount.value = response.data.unread || 0
      return latestNotification.value
    } catch (error) {
      console.error('Failed to fetch latest notification:', error)
      return null
    }
  }

  async function markAsRead(notificationId: number): Promise<boolean> {
    try {
      const response = await api.post(`/notifications/${notificationId}/read`)
      
      if (response.data.success) {
        // Update local state
        const notification = notifications.value.find(n => n.id === notificationId)
        if (notification && notification.pivot) {
          notification.pivot.read_at = new Date().toISOString()
        }
        unreadCount.value = Math.max(0, unreadCount.value - 1)
        return true
      }
      return false
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
      return false
    }
  }

  return {
    notifications,
    unreadCount,
    loading,
    latestNotification,
    hasUnread,
    fetchAll,
    fetchLatest,
    markAsRead
  }
})
